import axios from 'axios'
import { call, delay, put, type SagaGenerator } from 'typed-redux-saga'
import type { SagaReturnType } from 'redux-saga/effects'
import { setPageError } from 'store/ui/actions'

// Any function these helpers can `call`: a plain function, a promise-returning
// function, or another saga. `never[]` parameters make the constraint accept
// every arity while `Parameters<Fn>` still resolves the real argument tuple at
// each call site, so a caller's argument types (and the result type flowing back
// out of `yield*`) are checked against the wrapped function.
type CallableFn = (...args: never[]) => unknown

// A saga (or plain) function wrapped by errorWrap. Generic over its own argument
// tuple so callers thread real arg types through.
type WrappedFn<Args extends unknown[]> = (...args: Args) => unknown

// createTimeoutError attaches the polling task to a named Error so callers can
// inspect what timed out. The task is opaque here (a polling response object).
interface TimeoutError extends Error {
  task: unknown
}

export function createTimeoutError(task: unknown): TimeoutError {
  const error = new Error('Task polling timed out') as TimeoutError
  error.name = 'TimeoutError'
  error.task = task
  return error
}

export const errorWrap = <Args extends unknown[]>(
  saga: WrappedFn<Args>,
  FAIL_ACTION_CONSTANT?: string | null,
  onError?: (e: unknown, ...args: Args) => unknown
) =>
  function* (...args: Args): SagaGenerator<void> {
    try {
      yield* call(saga, ...args)
    } catch (e) {
      // Properly format errors before logging to avoid "[object XMLHttpRequest]" errors
      if (axios.isAxiosError(e)) {
        console.error(e.toJSON())
      } else if (e instanceof Error) {
        console.error(e)
      } else {
        // Handle cases where e might be a raw XMLHttpRequest or other non-Error object
        console.error('Error:', e)
      }

      if (FAIL_ACTION_CONSTANT) {
        yield* put({
          type: FAIL_ACTION_CONSTANT,
          error: e
        })
      }

      if (onError) {
        yield* call(onError, e, ...args)
      }
    }
  }

export function* retryUnless<Fn extends CallableFn>(
  maxRetries: number,
  delayMs: number,
  noRetryStatuses: number[],
  fn: Fn,
  ...args: Parameters<Fn>
): SagaGenerator<SagaReturnType<Fn>> {
  let lastError: unknown
  for (let i = 0; i < maxRetries; i++) {
    try {
      return yield* call(fn, ...args)
    } catch (e) {
      lastError = e
      if (axios.isAxiosError(e) && e.response && noRetryStatuses.includes(e.response.status)) {
        throw e
      }
      if (i < maxRetries - 1) {
        yield* delay(delayMs)
      }
    }
  }
  throw lastError
}

// Statuses that should fail fast rather than retry — client errors, not transient.
export const NO_RETRY_STATUSES = [400, 401, 403, 404, 422]

// Default backoff between retry attempts.
export const RETRY_DELAY_MS = 3000

// Retry a saga call on transient server errors only (e.g. 503), failing fast on
// client (4xx) errors. Wraps retryUnless with the standard delay/status policy so
// call sites don't repeat the delay and no-retry list.
export function* retryTransient<Fn extends CallableFn>(
  maxRetries: number,
  fn: Fn,
  ...args: Parameters<Fn>
): SagaGenerator<SagaReturnType<Fn>> {
  // retryUnless pinned to this call's Fn so `call`'s Parameters<Fn> resolves; a
  // bare reference to the generic leaves the arg tuple unresolved.
  return yield* call(retryUnless<Fn>, maxRetries, RETRY_DELAY_MS, NO_RETRY_STATUSES, fn, ...args)
}

//
// Forward data loading errors to React app
// (e.g. if we received 404 or 500 when loading a resource)
//
export const forwardPageError = (forwardList: number[] | '*' = [404]) =>
  function* (e: unknown): SagaGenerator<void> {
    const statusCode = axios.isAxiosError(e) ? e.response?.status : undefined
    if (statusCode === undefined) {
      return
    }
    if (forwardList === '*') {
      if (statusCode >= 400) {
        yield* put(setPageError({ status: statusCode }))
      }
    } else if (forwardList.includes(statusCode)) {
      yield* put(setPageError({ status: statusCode }))
    }
  }
